/**
 * +field: Group Finder companions (docs/design/zones.md "Dungeons"). The bots that fill a dungeon
 * party are not stored on the profile: each is compiled from starter gear at the hero's level, and
 * its persona (chat, AFKs, moods) is a stranger generated from the run seed. Pure.
 */
import type { ClassDef, ClassId, Role } from '@keystone/sim';
import { baseClassIdOf, specClassId } from '@keystone/sim';
import { BASE_CLASSES, SPECS, SPEC_LEVEL, type Persona, generatePersona, getContentBundle, specClass } from '@keystone/content';
import { CLASS_STAT_PROFILES } from './data';
import { compileCharacter } from './compile';
import { starterGear } from './items';
import { tierDef } from './rating';
import { defaultLoadout } from './talents';
import { LEVEL_CAP } from './leveling';
import { gridPointsAt, gridRoleOf, suggestedGrid } from './grid'; // +grid
import type { CharacterProgress, CompiledCharacter, EquippedGear } from './types';

function roleFor(classDef: Pick<ClassDef, 'role'> | undefined, classId: ClassId): ClassDef['role'] {
  return classDef?.role ?? getContentBundle().classes.find((c) => c.id === classId)?.role ?? 'dps';
}

/** A companion's starter gear (ids `gf-<class>-<n>`, never in any inventory). */
export function companionGear(classId: ClassId): EquippedGear {
  const priority = CLASS_STAT_PROFILES[classId]?.priority ?? ['haste', 'crit', 'expertise', 'spirit'];
  let n = 0;
  const gear: EquippedGear = {};
  for (const it of starterGear(classId, priority, () => `gf-${classId}-${++n}`)) gear[it.slot] = it;
  return gear;
}

/**
 * A Group Finder companion as a (throwaway) CharacterProgress: starter gear, the recommended
 * talents for `level` points (Novice talent tiers). Not stored on the profile.
 */
export function companionCharacter(classId: ClassId, level = 1, classDef?: Pick<ClassDef, 'role'>): CharacterProgress & { items: EquippedGear } {
  const items = companionGear(classId);
  const lvl = Math.max(1, Math.min(LEVEL_CAP, Math.floor(level) || 1));
  const loadout = defaultLoadout(roleFor(classDef, classId), lvl, tierDef('novice').talentTiers, classId);
  const gear: CharacterProgress['gear'] = {};
  for (const [slot, it] of Object.entries(items)) gear[slot as keyof typeof gear] = it!.id;
  return { classId, hero: { level: lvl, xp: 0, talentPoints: lvl }, gear, loadouts: [loadout], activeLoadoutId: loadout.id, autoManaged: true, items };
}

/**
 * Compile a Group Finder companion against its base ClassDef (starter gear, recommended talents at
 * `level`). +specs: a spec-keyed class id ("warrior@ironclad", groupFinderParty) compiles the base
 * with that spec folded in, registered under the keyed id (bundleWithCompiled adds it).
 */
export function compileCompanion(classDef: ClassDef, level = 1): CompiledCharacter {
  const ch = companionCharacter(classDef.baseClassId ?? classDef.id, level, classDef);
  // +grid: strangers bring the suggested Waystone Grid path for their class / spec role at this level.
  const grid = companionGrid(classDef, ch.hero.level);
  return { ...compileCharacter(classDef, ch.items, ch.loadouts[0]!.nodes, { trustTalents: true, ...(grid.length ? { grid } : {}) }), classId: classDef.id };
}

/** +grid: a Group Finder stranger's / bot's grid: the suggested path at the points a hero of `level` has. */
export function companionGrid(classDef: ClassDef, level: number): string[] {
  return suggestedGrid(classDef.baseClassId ?? classDef.id, gridRoleOf(classDef), gridPointsAt(level), [], classDef);
}

/** One Group Finder slot: the registry class key, its base class and spec (null = the base kit). */
export interface PartySlot {
  /** Registry class id: the hero's base id, a stranger's base id, or a spec key ("warrior@ironclad"). */
  classId: ClassId;
  baseClassId: ClassId;
  specId: string | null;
  role: Role;
  hero: boolean;
}

const ROLE_ORDER: readonly Role[] = ['tank', 'healer', 'dps', 'dps'];

/**
 * +specs: a Group Finder party in slot order (tank, healer, dps, dps) around the hero, by ROLE: the
 * hero takes the first open slot of its role (a Mirage Rogue tanks, a Grovewarden Ranger heals, a
 * Heretic Acolyte deals damage). Strangers at level 10+ get a random implemented spec of the slot's
 * role (spec-keyed ids, so a Ravager hero can still queue with an Ironclad stranger); below 10, or
 * for a class with no implemented spec of that role, a base class whose own role fits. Deterministic
 * in `seed`; never two identical class keys, and the hero's own base id is never reused.
 */
export function groupFinderParty(heroClassId: ClassId, heroRole: Role, level: number, seed: number): PartySlot[] {
  const heroSlot = Math.max(0, ROLE_ORDER.indexOf(heroRole));
  const taken = new Set<string>([heroClassId]);
  const usedBases = new Set<string>([heroClassId]);
  const out: PartySlot[] = [];
  ROLE_ORDER.forEach((role, slot) => {
    if (slot === heroSlot) {
      out.push({ classId: heroClassId, baseClassId: heroClassId, specId: null, role: heroRole, hero: true });
      return;
    }
    const opts: Omit<PartySlot, 'hero'>[] = [];
    if (level >= SPEC_LEVEL) for (const s of SPECS) if (s.role === role) opts.push({ classId: specClassId(s.baseClassId, s.id), baseClassId: s.baseClassId, specId: s.id, role });
    for (const c of BASE_CLASSES) {
      if (c.role !== role) continue;
      // At 10+ a class with an implemented spec of this role sends that spec, not its base kit.
      if (level >= SPEC_LEVEL && SPECS.some((s) => s.baseClassId === c.id && s.role === role)) continue;
      opts.push({ classId: c.id, baseClassId: c.id, specId: null, role });
    }
    // Nobody fits (e.g. a low-level party around a spec'd hero): any implemented spec of the role steps in.
    if (!opts.some((o) => !taken.has(o.classId))) for (const s of SPECS) if (s.role === role) opts.push({ classId: specClassId(s.baseClassId, s.id), baseClassId: s.baseClassId, specId: s.id, role });
    const free = opts.filter((o) => !taken.has(o.classId));
    // Prefer a base class nobody in the party plays yet (variety), else any free option.
    const fresh = free.filter((o) => !usedBases.has(o.baseClassId));
    const pool = fresh.length ? fresh : free;
    if (!pool.length) throw new Error(`groupFinderParty: no ${role} for slot ${slot}`);
    const pick = pool[pugSeed(seed, slot) % pool.length]!;
    taken.add(pick.classId);
    usedBases.add(pick.baseClassId);
    out.push({ ...pick, hero: false });
  });
  return out;
}

/** The registry ClassDef for a Group Finder slot's class id (base, or a spec key). */
export function partyClassDef(classId: ClassId): ClassDef | undefined {
  const base = BASE_CLASSES.find((c) => c.id === baseClassIdOf(classId));
  if (!base) return getContentBundle().classes.find((c) => c.id === classId);
  const at = classId.indexOf('@');
  return at < 0 ? base : specClass(base, classId.slice(at + 1), true);
}

/** Seed of the Group Finder stranger in `slot` of a key with run seed `runSeed`. */
export function pugSeed(runSeed: number, slot: number): number {
  let h = Math.imul((runSeed >>> 0) ^ 0x9e3779b9, 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (slot + 1) ^ (h >>> 15), 0xc2b2ae35) >>> 0;
  return (((h ^ (h >>> 13)) >>> 0) % 0x7ffffffe) + 1;
}

export interface SlotPersona {
  slot: number;
  classId: ClassId;
  persona: Persona;
}

/**
 * Personas for every bot slot of a key (slot order of `classIds`; the human's class skipped, pass
 * '' to fill all four, e.g. spectating): Group Finder strangers generated from `runSeed` + slot,
 * names made unique. Deterministic, so the client's session and a party room agree.
 */
export function groupFinderPersonas(mainClassId: ClassId, classIds: readonly ClassId[], runSeed: number): SlotPersona[] {
  const taken = new Set<string>();
  const out: SlotPersona[] = [];
  classIds.forEach((classId, slot) => {
    if (classId === mainClassId) return;
    const seed = pugSeed(runSeed, slot);
    const persona = generatePersona({ classId, seed }); // +specs: a spec key stays the persona's class id (bots match personas to units by it)
    for (let n = 0; taken.has(persona.name); n++) persona.name = `${persona.name.replace(/\d+$/, '')}${((seed + n * 7) % 89) + 10}`;
    taken.add(persona.name);
    out.push({ slot, classId, persona });
  });
  return out;
}

/** Just the personas (startSession's `personas` option). The first argument is ignored (kept for callers' signature). */
export function companionPersonas(_profile: unknown, mainClassId: ClassId, classIds: readonly ClassId[], runSeed: number): Persona[] {
  return groupFinderPersonas(mainClassId, classIds, runSeed).map((x) => x.persona);
}
